import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ExternalLink } from '../../components';

import css from './TermsOfService.module.css';

const TermsOfService = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.root, className);

  // prettier-ignore
  return (
    <div className={classes}>
      <p>
        Welcome to ROTATE, a curated online marketplace connecting buyers and sellers
        of quality once-loved furniture and homewares. These Terms and conditions form
        a legally binding agreement between you and ROTATE NZ LIMITED. By using
        our platform, you agree to the following terms and conditions:
      </p>

      <p className={css.subheading}><strong>1. Marketplace Nature</strong></p>
      <p>
        ROTATE is an online marketplace designed to connect members who want to buy
        or sell once-loved and pre-owned interiors and homewares. Our platform allows
        sellers to list items in a fixed-price format, and buyers to make purchases
        or submit offers. Sellers can accept or decline offers at their discretion.
        Once an offer is accepted, the sale is binding on both parties and subject
        to the same terms as a full-price transaction.
      </p>
      <p>
        ROTATE does not inspect, possess, or guarantee any item listed on the platform.
        We do not verify the identity of users or pre-screen listings. The legal
        ownership of any item passes directly from seller to buyer at the point of
        sale, and ROTATE is not a party to any transaction between users. You use
        ROTATE at your own risk.
      </p>

      <p className={css.subheading}><strong>2. Eligibility and Account Use</strong></p>
      <p>
        In order to sell or buy on ROTATE, you must create an account. To sell,
        you are also required to create and link a Stripe account for payments.
        Stripe is a secure global payment platform that facilitates transactions
        on ROTATE. You will need to provide identity verification, banking details,
        and comply with Stripe’s requirements, which are designed to meet anti-money
        laundering and Know Your Customer (KYC) obligations. For more information,
        visit Stripe at
        &nbsp;
        <ExternalLink
          key="stripe_connect"
          href="https://stripe.com/nz/connect"
          className={css.inlineLink}
        >
          https://stripe.com/nz/connect
        </ExternalLink>.
      </p>
      <p>
        Users may be able to leave feedback about their transactions. You agree
        not to post false, misleading or defamatory reviews. ROTATE may moderate
        or remove feedback at its discretion.
      </p>
      <p>
        To use ROTATE, you must be at least 18 years old and capable of forming
        a legally binding contract. Individuals under the age of 18 must use
        the platform under the supervision of a parent or legal guardian who takes
        full responsibility for their activity. You may not use ROTATE if you have
        been previously suspended or removed.
      </p>
      <p>
        You are responsible for maintaining the confidentiality of your login
        credentials and all activities conducted under your account. Accounts are
        non-transferable. You may not sell, assign, or allow others to access your
        account. All account activity is your sole responsibility.
      </p>
      <p>
        ROTATE reserves the right to suspend or terminate access for any reason at
        its sole discretion. ROTATE also reserves the right to delay or withhold
        disbursement of funds while a dispute is being investigated.
      </p>

      <p className={css.subheading}><strong>2A. Code of Conduct</strong></p>
      <p>
        We expect all members of the ROTATE community to act with honesty, courtesy,
        and good faith in all interactions. The following behaviours are expected:
      </p>
      <ul>
        <li>Do not post misleading, abusive, or retaliatory feedback.</li>
        <li>Respond to messages and offers promptly.</li>
        <li>Avoid spam or unsolicited messages to other users.</li>
        <li>Do not attempt to solicit or complete sales outside of the platform.</li>
      </ul>
      <p>
        Sellers must not pressure buyers, and buyers must not make false claims or
        engage in manipulative behaviour. Breaching the Code of Conduct may result
        in removal from the platform.
      </p>

      <p className={css.subheading}><strong>3. Fees and Commission</strong></p>
      <p>
        It is free to list an item on ROTATE. ROTATE charges a 15% commission on
        the final sale price of each item sold plus any associated
        &nbsp;
        <ExternalLink
          className={css.inlineLink}
          key="Stripe fees"
          href="https://stripe.com/nz/pricing?utm_campaign=APAC_NZ_EN_Search_Brand_Core_EXA-PHR-21292808761&utm_medium=cpc&utm_source=google&ad_content=703668270174&utm_term=stripe%20fees%20nz&utm_matchtype=e&utm_adposition=&utm_device=c&gad_source=1&gad_campaignid=21292808761&gbraid=0AAAAADKNRO5TuTIa3g_RUMue7sRHZ6Ffu&gclid=Cj0KCQjwgvnCBhCqARIsADBLZoIDzl5xB6AP8fBpqbFWUIBL69WG6yIl84hl4vinyL0T_l-YNdE0MowaAkz5EALw_wcB"
        >
          Stripe fees
        </ExternalLink>
        &nbsp;.
        This commission is automatically deducted from the seller’s proceeds at
        the time of payout, and is processed through Stripe, ROTATE’s third-party
        payment provider. Sellers must not attempt to avoid this commission by
        redirecting transactions off-platform or using external communication to finalise sales.
      </p>

      <p className={css.subheading}><strong>4. Seller Responsibilities</strong></p>
      <p>
        ROTATE is a marketplace for quality pre-owned interiors — we call them once-loved.
        Sellers may list both new and second-hand homewares, as long as they are
        authentic, quality, in good condition and meet ROTATE’s standards. If you
        believe we are missing anything, please contact
        &nbsp;
        <ExternalLink
          className={css.inlineLink}
          key="rotatenow_email"
          href="mailto:hq@rotatenow.co.nz"
        >
          hq@rotatenow.co.nz
        </ExternalLink>.
      </p>
      <p>
        Listings marked as 'vintage' will be reviewed before being approved to ensure
        they meet our standards based on the item's originality, condition, quality,
        material, era, rarity and style. ROTATE reserves the right to decline, remove
        or moderate any listing that does not meet our standards or violates our terms,
        at our sole discretion.
      </p>
      <p>Sellers must:</p>
      <ul>
        <li>
          Ensure all information provided in their listings is accurate, lawful,
          and not misleading. Sellers are solely responsible for the legality and
          accuracy of their listings.
        </li>
        <li>
          Accurately describe each item, including but not limited to condition,
          materials, measurements, defects, era, provenance and other.
        </li>
        <li>Provide clear, original images where appropriate.</li>
        <li>Be the legal owner of items and authorised to sell them.</li>
        <li>Coordinate with buyers to arrange delivery or pick-up. </li>
        <li>Dispatch items within agreed timeframes.</li>
      </ul>
      <p>
        Listings must not include counterfeit or replica items. Only authentic products
        manufactured or approved for retail by the original designer may be listed.
      </p>
      <p>
        ROTATE strongly recommends using tracked courier services for all deliveries.
        Sellers may charge a reasonable shipping fee to cover packaging and postage.
        Items sent without tracking are at the seller’s own risk — ROTATE does not
        accept responsibility for lost, untracked, or damaged parcels.
      </p>
      <p>
        Misrepresentation, repeated cancellations, or fee avoidance will result
        in suspension or removal.
      </p>

      <p className={css.subheading}><strong>5. Buyer Responsibilities</strong></p>
      <p>Buyers must:</p>
      <ul>
        <li>Carefully review item details and photos before committing to purchase.</li>
        <li>Promptly coordinate with the seller for delivery or pick-up.</li>
        <li>Inspect items upon receipt and confirm acceptance.</li>
        <li>Only communicate through ROTATE and avoid off-platform transactions.</li>
      </ul>
      <p>
        All sales are final upon buyer acceptance. If there is an issue, buyers
        must contact the seller within 24 hours of receiving the item. ROTATE is
        not responsible for disputes related to item condition after this period,
        or for any issues resulting from communication or payment made outside the platform.
      </p>

      <p className={css.subheading}><strong>5A. Returns and Refunds</strong></p>
      <p>
        ROTATE is a peer-to-peer marketplace built on trust and true representation.
        All sales are considered final once the buyer confirms acceptance of the item.
        ROTATE does not facilitate returns, refunds, or exchanges unless required
        by New Zealand consumer law.
      </p>
      <p>
        Buyers are responsible for inspecting items upon delivery and must report
        any issues including but not limited to misrepresentations to the seller
        within 24 hours of receipt. After this period, ROTATE is not responsible
        for item-related disputes.
      </p>
      <p>
        ROTATE uses Stripe Connect to process payments and temporarily holds funds
        before releasing them to sellers. If a buyer raises a concern within the 24-hour
        window and cannot resolve the issue directly with the seller, the buyer may
        request ROTATE to review the case. ROTATE may, at its sole discretion, issue
        a refund based on but not limited to the communication, evidence received
        and transaction history. Please note:
      </p>
      <p>
        Refunds are not guaranteed and are assessed on a case-by-case basis.
      </p>
      <p>
        Refunds are issued from ROTATE’s platform account; sellers do not issue
        refunds directly.
      </p>
      <p>
        If funds have already been released to the seller, ROTATE may recover
        the amount from future payouts.
      </p>
      <p>
        Stripe’s transaction fees are non-refundable** and may be deducted from
        the refunded amount.
      </p>
      <p>
        In the event a buyer files a chargeback with their bank instead of going
        through ROTATE, Stripe manages the process. ROTATE may provide supporting
        documentation but is not liable for the outcome of chargeback cases.
      </p>
      <p>
        Sellers are expected to accurately describe items, include clear photos, and
        respond to buyer concerns in good faith. Delays in responding or misrepresentation
        may affect ROTATE’s ability to support the seller in the event of a dispute.
      </p>

      <p className={css.subheading}><strong>6. Payments and Disbursements</strong></p>
      <p>
        Sellers are solely responsible for determining and meeting any tax obligations,
        including GST, arising from the sale of their items on ROTATE. Buyers are
        responsible for any additional taxes, duties, or customs charges that may
        apply based on their location or purchase.
      </p>
      <p>
        Buyers pay directly via ROTATE’s secure Stripe payment system. Sellers are
        required to maintain valid and accurate bank details linked through their
        Stripe account. Failure to do so may result in payment delays that ROTATE
        cannot resolve. Please contact Stripe if you have any issues regarding payments.
      </p>
      <p>
        Once the buyer has confirmed receipt and acceptance of the item, the seller’s
        payment (less the 15% commission and any applicable Stripe transaction fees)
        will be automatically disbursed.
      </p>
      <p>
        ROTATE communicates important information and updates via email only. It is
        your responsibility to maintain an accurate email address and check your
        inbox regularly. ROTATE is not responsible for delays or errors caused by
        third-party payment providers, incorrect details, or inaction on your part.
      </p>

      <p className={css.subheading}><strong>7. Disputes Between Users</strong></p>
      <p>
        Disputes must be reported within 24 hours of item receipt, including concerns
        about damage, incorrect items, or missing parcels. Buyers are encouraged
        to contact the seller directly through ROTATE first to try and resolve
        the matter amicably. If the issue remains unresolved, ROTATE may assist
        by reviewing communication records and available transaction details, but
        is not obligated to intervene or offer resolution. Buyers and sellers are
        expected to act in good faith and use reasonable efforts to resolve disputes.
        Users are expected to attempt resolution in good faith.
      </p>

      <p className={css.subheading}><strong>8. Disputes with ROTATE</strong></p>
      <p>
        Any legal claims against ROTATE must be filed under the laws of New Zealand
        and resolved in the courts of New Zealand.
      </p>

      <p className={css.subheading}><strong>9. Prohibited Items and Activities</strong></p>
      <p>
        ROTATE is committed to maintaining a safe, trusted environment for all
        members of our community. You must not:
      </p>
      <ul>
        <li>List incomplete, broken, unsafe, counterfeit, or unlawful items.</li>
        <li>Violate any intellectual property rights.</li>
        <li>Mislead other users or impersonate others.</li>
        <li>Attempt to avoid platform fees.</li>
        <li>Solicit, spam, or harass others.</li>
      </ul>
      <p>
        ROTATE reserves the right to remove listings or suspend accounts for any
        prohibited activity, without prior notice.
      </p>

      <p className={css.subheading}><strong>10. Intellectual Property and User Content</strong></p>
      <p>
        You retain ownership of content you upload but grant ROTATE a worldwide,
        non-exclusive, perpetual, royalty-free, fully paid-up licence to use, reproduce,
        reformat, adapt, distribute, excerpt, and display such content for promotional
        and operational purposes. This includes usage on the ROTATE website, mobile app,
        marketing materials, and social media.
      </p>
      <p>
        You also agree that ROTATE may store, translate, or reformat member-submitted
        content and allow buyers to reuse the original seller’s images and descriptions
        when re-listing a purchased item. You confirm that your uploaded content
        does not violate any third-party rights or applicable laws. ROTATE reserves
        the right to remove or moderate any content it considers inappropriate,
        misleading, or in breach of this agreement. Buyers may reuse product descriptions
        and images when relisting an item purchased through ROTATE. Use of ROTATE's
        content, branding, or code without written permission is strictly prohibited.
      </p>

      <p className={css.subheading}><strong>11. Verified Rights Owner (VRO) Programme</strong></p>
      <p>
        ROTATE has a Verified Rights Owner (VRO) programme to help intellectual
        property rights holders protect their work. If you believe your trademark,
        copyright, or other IP rights are being infringed by a listing or content
        on our platform, you may submit a takedown request.
      </p>
      <p>The request must include:</p>
      <ul>
        <li>Proof of ownership (e.g., trademark or copyright registration)</li>
        <li>A description of the infringing content and its location (e.g., listing link)</li>
        <li>Your contact details and a good-faith statement</li>
        <li>A signature or electronic equivalent</li>
      </ul>
      <p>
        Send VRO claims to
        &nbsp;
        <ExternalLink
          className={css.inlineLink}
          key="rotatenow_email2"
          href="mailto:hq@rotatenow.co.nz"
        >
          hq@rotatenow.co.nz
        </ExternalLink>.
        We may notify the other party and provide them with the opportunity to respond.
        Repeat infringers may be suspended or removed from the platform. for content
        that infringes intellectual property. Requests must include evidence of
        ownership and a good faith claim. ROTATE may remove infringing content and
        suspend offending accounts. Takedown correspondence should be sent to
        &nbsp;
        <ExternalLink
          className={css.inlineLink}
          key="rotatenow_email3"
          href="mailto:hq@rotatenow.co.nz"
        >
          hq@rotatenow.co.nz
        </ExternalLink>.
      </p>

      <p className={css.subheading}><strong>12. Personal Information and Privacy</strong></p>
      <p>
        Use of ROTATE is subject to our Privacy Policy, which can be found at
        &nbsp;
        <ExternalLink
          className={css.inlineLink}
          key="rotatenow_co_nz_privacy"
          href="https://www.rotatenow.co.nz/privacy-policy"
        >
          www.rotatenow.co.nz/privacy-policy
        </ExternalLink>.
      </p>
      <p>
        ROTATE collects and processes personal information such as your name, contact
        details, location, transaction history, and platform usage to operate our
        services, facilitate transactions, provide support, and improve user experience.
        We may also collect device and usage data through cookies and analytics tools.
      </p>
      <p>
        Your personal information may be shared with trusted third parties such
        as payment processors, couriers, legal authorities (if required), and marketing
        or analytics providers, but only to the extent necessary to fulfil a transaction,
        comply with legal obligations, or improve our service. We take reasonable
        steps to protect personal information from loss, misuse, and unauthorised access.
      </p>
      <p>
        All personal data is stored in accordance with the New Zealand Privacy Act 2020.
        You have the right to access, update, or correct your personal data, and
        to request its deletion where appropriate. Requests can be sent to
        &nbsp;
        <ExternalLink
          className={css.inlineLink}
          key="rotatenow_co_nz_email4"
          href="mailto:hq@rotatenow.co.nz"
        >
          hq@rotatenow.co.nz
        </ExternalLink>.
      </p>
      <p>
        By using ROTATE, you consent to the collection, use, and disclosure of your
        personal information as outlined here and in our full Privacy Policy. Personal
        information obtained through a transaction may only be used to fulfil that
        transaction and must not be retained, shared, or used for marketing without consent.
      </p>

      <p className={css.subheading}><strong>13. Indemnity</strong></p>
      <p>
        You agree to indemnify and hold harmless ROTATE, its officers, directors,
        staff, and affiliates from any claims, losses, liabilities, and expenses
        (including legal fees) arising from your use of the platform, breach of terms,
        or violation of another’s rights.
      </p>

      <p className={css.subheading}><strong>14. Limitation of Liability</strong></p>
      <p>
        ROTATE may contain links to third-party websites or services. We are not
        responsible for the content, policies, or practices of any external sites.
        ROTATE provides the service on an "as is" and "as available" basis. We are
        not responsible for the performance, content, or security of third-party
        apps, plugins, browsers, or mobile platforms used to access the service.
        We do not guarantee continuous, uninterrupted, or secure access to ROTATE
        and are not liable for any interruptions, delays, or errors caused by
        circumstances beyond our control, including maintenance or upgrades. We are
        also not responsible for errors in listings caused by technical faults, user
        input errors, or system outages, and reserve the right to cancel or correct
        transactions if such errors are identified. To the maximum extent permitted
        by law, ROTATE disclaims all implied warranties. We are not liable for:
      </p>
      <ul>
        <li>Lost profits or revenue</li>
        <li>Platform outages or technical issues</li>
        <li>Third-party payment errors</li>
        <li>User disputes</li>
      </ul>
      <p>
        ROTATE’s total liability to you is limited to the amount of commission ROTATE
        received from the specific transaction in question. This limitation reflects
        ROTATE’s role as a neutral platform provider and not a direct party to
        transactions between buyers and sellers.
      </p>

      <p className={css.subheading}><strong>15. Right to Refuse Service</strong></p>
      <p>
        ROTATE may suspend or terminate access for any reason, including but not
        limited to abuse of the platform, violation of policies, fraudulent activity,
        or attempts to avoid platform fees. We may take action without prior notice.
        We reserve the right to investigate and act upon patterns of suspicious
        behaviour, including repeated complaints, attempts to circumvent ROTATE
        systems, or other conduct we determine may harm the integrity of the platform.
      </p>

      <p className={css.subheading}><strong>16. Change of Ownership</strong></p>
      <p>
        If ROTATE is sold or merged, user data may be transferred under the same
        privacy obligations. You will be notified of any material changes.
      </p>

      <p className={css.subheading}><strong>17. Force Majeure</strong></p>
      <p>
        We are not responsible for delays or failure to perform due to events beyond
        our control, including but not limited to natural disasters, cyberattacks,
        outages, strikes, or legal changes.
      </p>

      <p className={css.subheading}><strong>18. Severability and Relationship</strong></p>
      <p>
        If any term is found unenforceable, it will be modified to reflect intent
        and the remaining terms will remain in force. No joint venture, employment,
        or agency relationship is created by these terms.
      </p>

      <p className={css.subheading}><strong>19. Amendments</strong></p>
      <p>
        These terms may change from time to time. Users will be notified of material
        updates via the platform. Continued use of ROTATE means you accept the revised terms.
      </p>
      <p>Thank you for being part of the ROTATE community.</p>
    </div>
  );
};

TermsOfService.defaultProps = {
  rootClassName: null,
  className: null,
};

const { string } = PropTypes;

TermsOfService.propTypes = {
  rootClassName: string,
  className: string,
};

export default TermsOfService;
